import { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  ClockIcon, ExclamationTriangleIcon,
  CheckCircleIcon, XCircleIcon, ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ta * { box-sizing:border-box; }
  .ta { font-family:'Sora',sans-serif; color:#1a1d2e; animation:taFade .4s ease both; }
  @keyframes taFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }

  /* ── Header ── */
  .ta-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; margin-bottom:3px; }
  .ta-sub   { font-size:.76rem; color:#9094a8; margin-bottom:1.5rem; }

  /* ── Toolbar ── */
  .ta-toolbar { display:flex; gap:.7rem; flex-wrap:wrap; align-items:center; margin-bottom:1.2rem; }
  .ta-date { font-family:'JetBrains Mono',monospace; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:white; outline:none; }
  .ta-date:focus { border-color:#10c98e; }
  .view-tabs { display:flex; gap:5px; }
  .view-btn  { padding:7px 18px; border-radius:9px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.76rem; font-weight:600; cursor:pointer; color:#6b7194; transition:all .15s; }
  .view-btn:hover  { border-color:#10c98e; color:#10c98e; }
  .view-btn.active { background:#10c98e; color:white; border-color:#10c98e; }
  .export-group { display:flex; gap:5px; margin-left:auto; }
  .btn-export { display:inline-flex; align-items:center; gap:5px; padding:7px 14px; border:1.5px solid #e0e4f0; border-radius:9px; background:white; font-family:'Sora',sans-serif; font-size:.76rem; font-weight:600; cursor:pointer; color:#6b7194; transition:all .15s; }
  .btn-export svg { width:14px; height:14px; stroke-width:2; }
  .btn-export.csv:hover  { background:#ecfdf5; color:#10c98e; border-color:#10c98e; }
  .btn-export.xlsx:hover { background:#eef2ff; color:#3b6ef8; border-color:#3b6ef8; }

  /* ── Check-in banner ── */
  .checkin-card { background:linear-gradient(135deg,#10c98e,#3b6ef8); border-radius:16px; padding:1.4rem 1.6rem; color:white; margin-bottom:1.2rem; display:flex; align-items:center; justify-content:space-between; gap:1rem; flex-wrap:wrap; }
  .checkin-card.out  { background:linear-gradient(135deg,#f59e0b,#ef4444); }
  .checkin-card.done { background:linear-gradient(135deg,#6b7194,#4a5075); }
  .cc-label  { font-size:.78rem; font-weight:600; opacity:.85; margin-bottom:4px; }
  .cc-status { font-size:1.1rem; font-weight:700; }
  .cc-times  { display:flex; gap:1.5rem; flex-wrap:wrap; }
  .cc-time   { text-align:center; }
  .cc-tl     { font-size:.65rem; opacity:.7; font-weight:600; text-transform:uppercase; letter-spacing:.06em; }
  .cc-tv     { font-size:1rem; font-weight:700; font-family:'JetBrains Mono',monospace; margin-top:2px; }
  .btn-white { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; background:rgba(255,255,255,.2); color:white; border:1.5px solid rgba(255,255,255,.4); backdrop-filter:blur(4px); }
  .btn-white:hover:not(:disabled) { background:rgba(255,255,255,.35); }
  .btn-white:disabled { opacity:.5; cursor:not-allowed; }
  .btn-white svg { width:15px; height:15px; stroke-width:2; }

  /* ── Monthly summary bar (image 3 style) ── */
  .month-bar { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1rem 1.4rem; margin-bottom:1rem; display:flex; align-items:center; flex-wrap:wrap; gap:.5rem; }
  .mb-title  { font-size:.9rem; font-weight:700; color:#0f1123; white-space:nowrap; margin-right:.8rem; }
  .mb-divider { width:1px; background:#f0f2f7; align-self:stretch; margin:0 .2rem; }
  .mb-items  { display:flex; flex:1; min-width:0; }
  .mb-item   { flex:1; min-width:68px; text-align:center; padding:0 .6rem; border-left:1px solid #f0f2f7; }
  .mb-item:first-child { border-left:none; }
  .mb-lbl    { font-size:.6rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; margin-bottom:5px; }
  .mb-val    { width:30px; height:30px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; font-size:.88rem; font-weight:700; }

  /* ── Table panel ── */
  .table-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .att-tbl-wrap { overflow-x:auto; }
  .att-tbl { width:100%; border-collapse:collapse; }
  .att-tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .att-tbl th { padding:10px 16px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .att-tbl tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .att-tbl tbody tr:last-child { border-bottom:none; }
  .att-tbl tbody tr:hover { background:#fafbff; }
  .att-tbl td { padding:11px 16px; font-size:.8rem; vertical-align:middle; text-align:center !important; }

  /* ── Status pills ── */
  .sp { display:inline-flex; align-items:center; font-size:.67rem; font-weight:600; padding:3px 10px; border-radius:99px; }
  .sp.present  { background:#ecfdf5; color:#10c98e; }
  .sp.absent   { background:#fef2f2; color:#ef4444; }
  .sp.on-leave { background:#eef2ff; color:#3b6ef8; }
  .sp.holiday  { background:#f3f0ff; color:#8b5cf6; }
  .sp.off      { background:#f0f2f7; color:#6b7194; }
  .mono        { font-family:'JetBrains Mono',monospace; font-size:.76rem; color:#6b7194; }
  .amber-text  { color:#f59e0b; font-weight:600; font-family:'JetBrains Mono',monospace; font-size:.76rem; }
  .red-text    { color:#ef4444; font-weight:600; font-family:'JetBrains Mono',monospace; font-size:.76rem; }

  /* ── Yearly grid (image 2 style) ── */
  .yr-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(260px,1fr)); gap:1rem; }
  .yr-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem 1.3rem; transition:box-shadow .15s; }
  .yr-card:hover { box-shadow:0 4px 18px rgba(0,0,0,.07); }
  .yr-card-title { font-size:.9rem; font-weight:700; color:#0f1123; margin-bottom:.9rem; }
  .yr-months-row { display:grid; grid-template-columns:repeat(6,1fr); gap:5px; margin-bottom:5px; }
  .yr-m     { text-align:center; }
  .yr-m-lbl { font-size:.58rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.04em; margin-bottom:3px; }
  .yr-m-bar { height:34px; border-radius:7px; display:flex; align-items:center; justify-content:center; font-size:.66rem; font-weight:700; }
  .yr-totals { display:flex; gap:10px; flex-wrap:wrap; padding-top:.75rem; margin-top:.4rem; border-top:1px solid #f0f2f7; align-items:center; }
  .yr-tot    { font-size:.7rem; font-weight:600; }

  /* ── States ── */
  .ta-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner    { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  .ta-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }

  /* ── Mobile cards ── */
  .att-cards { display:none; padding:10px; }
  .att-card  { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:12px 13px; margin-bottom:8px; }
  .att-card-top  { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; }
  .att-card-date { font-family:'JetBrains Mono',monospace; font-size:.8rem; font-weight:600; color:#0f1123; }
  .att-card-day  { font-size:.68rem; color:#9094a8; margin-top:1px; }
  .att-card-row  { display:flex; justify-content:space-between; align-items:center; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f0f2f8; }
  .att-card-row:last-child { border-bottom:none; }
  .att-card-lbl  { color:#9094a8; font-weight:600; }

  /* ── Responsive ── */
  @media (max-width:768px) {
    .ta-toolbar   { flex-direction:column; align-items:stretch; }
    .ta-date      { width:100%; }
    .export-group { margin-left:0; }
    .checkin-card { flex-direction:column; align-items:flex-start; gap:.8rem; }
    .btn-white    { width:100%; justify-content:center; }
    .month-bar    { flex-direction:column; align-items:flex-start; }
    .mb-items     { flex-wrap:wrap; width:100%; }
    .mb-item      { border-left:none; border-top:1px solid #f0f2f7; padding:.4rem 0 0; min-width:80px; }
    .att-tbl-wrap { display:none !important; }
    .att-cards    { display:block; }
  }
`;

const fmtTime   = (d) => d ? new Date(d).toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit', hour12:true }) : '—';
const fmtDate   = (d) => new Date(d).toLocaleDateString('en-US', { day:'numeric', month:'short' });
const dayOfWeek = (d) => new Date(d).toLocaleDateString('en-US', { weekday:'short' });
const curMonth  = () => new Date().toISOString().slice(0, 7);
const MONTHS    = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/* ── Shared attendance table ── */
const AttTable = ({ records }) => (
  <>
    <div className="att-tbl-wrap">
      <table className="att-tbl">
        <thead>
          <tr>
            <th>Date</th><th>Status</th><th>Check In</th><th>Check Out</th>
            <th>Hours</th><th>Late (min)</th><th>Early Exit (min)</th>
          </tr>
        </thead>
        <tbody>
          {records.map((r, i) => (
            <tr key={i}>
              <td>
                <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:'.78rem', fontWeight:500 }}>{fmtDate(r.date)}</div>
                <div style={{ fontSize:'.67rem', color:'#9094a8' }}>{dayOfWeek(r.date)}</div>
              </td>
              <td>
                {r.status === 'holiday'
                  ? <span className="sp holiday">🎉 {r.note || 'Holiday'}</span>
                  : r.status === 'off'
                  ? <span className="sp off">📅 Off Day</span>
                  : <span className={`sp ${r.status}`}>{r.status}{r.leaveType && r.status === 'on-leave' ? ` (${r.leaveType})` : ''}</span>
                }
              </td>
              <td><span className="mono">{fmtTime(r.checkIn)}</span></td>
              <td><span className="mono">{fmtTime(r.checkOut)}</span></td>
              <td><span className="mono">{r.workHours ? `${Number(r.workHours||0).toFixed(2)}h` : '—'}</span></td>
              <td><span className={r.lateMinutes > 0 ? 'amber-text' : 'mono'}>{r.lateMinutes > 0 ? `${r.lateMinutes}m` : '—'}</span></td>
              <td><span className={r.earlyExitMinutes > 0 ? 'red-text' : 'mono'}>{r.earlyExitMinutes > 0 ? `${r.earlyExitMinutes}m` : '—'}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {/* Mobile */}
    <div className="att-cards">
      {records.map((r, i) => (
        <div key={i} className="att-card">
          <div className="att-card-top">
            <div>
              <div className="att-card-date">{fmtDate(r.date)}</div>
              <div className="att-card-day">{dayOfWeek(r.date)}</div>
            </div>
            {r.status === 'holiday'
              ? <span className="sp holiday">🎉 {r.note || 'Holiday'}</span>
              : r.status === 'off'
              ? <span className="sp off">📅 Off Day</span>
              : <span className={`sp ${r.status}`}>{r.status}{r.leaveType && r.status === 'on-leave' ? ` (${r.leaveType})` : ''}</span>
            }
          </div>
          <div className="att-card-row"><span className="att-card-lbl">Check In</span><span className="mono">{fmtTime(r.checkIn)}</span></div>
          <div className="att-card-row"><span className="att-card-lbl">Check Out</span><span className="mono">{fmtTime(r.checkOut)}</span></div>
          {r.workHours > 0        && <div className="att-card-row"><span className="att-card-lbl">Hours</span><span className="mono">{Number(r.workHours||0).toFixed(2)}h</span></div>}
          {r.lateMinutes > 0      && <div className="att-card-row"><span className="att-card-lbl">Late</span><span className="amber-text">{r.lateMinutes}m</span></div>}
          {r.earlyExitMinutes > 0 && <div className="att-card-row"><span className="att-card-lbl">Early Exit</span><span className="red-text">{r.earlyExitMinutes}m</span></div>}
        </div>
      ))}
    </div>
  </>
);

/* ── Monthly summary bar — image 3 style ── */
const MonthBar = ({ label, summary }) => {
  const s = summary || {};
  const items = [
    { lbl:'Present',   val: s.present   || 0, color:'#10c98e', bg:'#ecfdf5' },
    { lbl:'Absent',    val: s.absent    || 0, color:'#ef4444', bg:'#fef2f2' },
    { lbl:'On Leave',  val: s.onLeave   || 0, color:'#3b6ef8', bg:'#eef2ff' },
    { lbl:'Holiday',   val: s.holiday   || 0, color:'#8b5cf6', bg:'#f5f3ff' },
    { lbl:'Late Days', val: s.lateCount || 0, color:'#f59e0b', bg:'#fffbeb' },
    { lbl:'Late Mins', val: s.totalLateMinutes ?? s.lateMinutes ?? 0, color:'#ef4444', bg:'#fff7ed' },
  ];
  return (
    <div className="month-bar">
      <div className="mb-title">{label}</div>
      <div className="mb-items">
        {items.map(({ lbl, val, color, bg }) => (
          <div key={lbl} className="mb-item">
            <div className="mb-lbl">{lbl}</div>
            <div className="mb-val" style={{ background: bg, color }}>{val}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Main component
══════════════════════════════════════════════════════════ */
const TeacherAttendance = () => {
  const [view,      setView]      = useState('daily');
  const [month,     setMonth]     = useState(curMonth());
  const [year,      setYear]      = useState(new Date().getFullYear());
  const [monthData, setMonthData] = useState(null);
  const [today,     setToday]     = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [actioning, setActioning] = useState(false);
  const [yearData,  setYearData]  = useState([]);
  const [yLoading,  setYLoading]  = useState(false);

  useEffect(() => { fetchToday(); }, []);
  useEffect(() => { fetchMonthly(); }, [month]);
  useEffect(() => { if (view === 'yearly') fetchYearly(); }, [year, view]);

  const fetchToday = async () => {
    try { const { data } = await api.get('/teacher/attendance/today'); setToday(data); } catch {}
  };

  const fetchMonthly = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/teacher/attendance?month=${month}`);
      setMonthData(data);
    } catch { toast.error('Failed to load attendance'); }
    finally  { setLoading(false); }
  };

  const fetchYearly = async () => {
    setYLoading(true);
    try {
      const months = await Promise.all(
        Array.from({ length: 12 }, (_, i) => {
          const m = String(i + 1).padStart(2, '0');
          return api.get(`/teacher/attendance?month=${year}-${m}`)
            .then(r  => ({ month: i+1, summary: r.data?.summary||{}, records: r.data?.records||[] }))
            .catch(() => ({ month: i+1, summary: {}, records: [] }));
        })
      );
      setYearData(months);
    } catch { toast.error('Failed to load yearly data'); }
    finally   { setYLoading(false); }
  };

  const handleCheckIn = async () => {
    try {
      setActioning(true);
      await api.post('/teacher/attendance/checkin');
      toast.success('Checked in!');
      fetchToday(); fetchMonthly();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally       { setActioning(false); }
  };

  const handleCheckOut = async () => {
    try {
      setActioning(true);
      await api.post('/teacher/attendance/checkout');
      toast.success('Checked out!');
      fetchToday(); fetchMonthly();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally       { setActioning(false); }
  };

  /* ── Export ── */
  const exportCSV = (rows, filename) => {
    if (!rows.length) return;
    const hdrs = Object.keys(rows[0]);
    const esc  = v => { const s = v==null?'':String(v); return s.includes(',')||s.includes('"')?'"'+s.replace(/"/g,'""')+'"':s; };
    const csv  = [hdrs.join(','), ...rows.map(r => hdrs.map(h => esc(r[h])).join(','))].join('\n');
    const blob = new Blob([csv], { type:'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href=url; a.download=filename+'.csv'; a.click(); URL.revokeObjectURL(url);
  };
  const exportData = async (rows, filename, fmt) => {
    if (!rows?.length) return;
    if (fmt==='csv') { exportCSV(rows, filename); return; }
    try { const X=await import('xlsx'); const ws=X.utils.json_to_sheet(rows); const wb=X.utils.book_new(); X.utils.book_append_sheet(wb,ws,'Sheet1'); X.writeFile(wb,filename+'.xlsx'); }
    catch { exportCSV(rows, filename); }
  };
  const handleExport = (fmt) => {
    if (view==='monthly' && monthData?.records?.length) {
      exportData(monthData.records.map(r => ({
        Date: new Date(r.date).toLocaleDateString('en-PK'),
        Day:  ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][new Date(r.date).getDay()],
        Status: r.status, 'Leave Type': r.leaveType||'',
        'Check In':  r.checkIn  ? fmtTime(r.checkIn)  : '',
        'Check Out': r.checkOut ? fmtTime(r.checkOut) : '',
        'Late (min)': r.lateMinutes||0, 'Early Exit (min)': r.earlyExitMinutes||0,
      })), `My_Attendance_${month}`, fmt);
    } else if (view==='yearly' && yearData.length) {
      exportData(yearData.flatMap(({ month:m, records }) => records.map(r => ({
        Month: `${year}-${String(m).padStart(2,'0')}`, Date: new Date(r.date).toLocaleDateString('en-PK'),
        Status: r.status, 'Leave Type': r.leaveType||'', 'Late (min)': r.lateMinutes||0,
      }))), `My_Attendance_${year}`, fmt);
    } else { toast.error('No data to export'); }
  };

  /* ── Derived state ── */
  const rec       = today?.record;
  const hasIn     = !!rec?.checkIn;
  const hasOut    = !!rec?.checkOut;
  const cardClass = hasOut ? 'done' : hasIn ? 'out' : '';

  const summary = monthData?.summary || (monthData?.records ? {
    present:          monthData.records.filter(r => r.status==='present').length,
    absent:           monthData.records.filter(r => r.status==='absent').length,
    onLeave:          monthData.records.filter(r => r.status==='on-leave').length,
    holiday:          monthData.records.filter(r => r.status==='holiday').length,
    lateCount:        monthData.records.filter(r => (r.lateMinutes||0)>0).length,
    totalLateMinutes: monthData.records.reduce((s,r) => s+(r.lateMinutes||0), 0),
  } : null);

  const monthLabel = new Date(month+'-01').toLocaleDateString('en-PK', { month:'long', year:'numeric' });

  /* ── Render ── */
  return (
    <div className="ta"><style>{style}</style>

      <div className="ta-title">My Attendance</div>
      <div className="ta-sub">Track your daily check-in / out and monthly record</div>

      {/* Toolbar */}
      <div className="ta-toolbar">
        {view === 'monthly' && <input type="month"  className="ta-date" value={month} onChange={e => setMonth(e.target.value)} />}
        {view === 'yearly'  && <input type="number" className="ta-date" value={year} min="2020" max="2099" onChange={e => setYear(Number(e.target.value))} style={{ width:100 }} />}

        <div className="view-tabs">
          {['daily','monthly','yearly'].map(v => (
            <button key={v} className={`view-btn ${view===v?'active':''}`} onClick={() => setView(v)}>
              {v.charAt(0).toUpperCase()+v.slice(1)}
            </button>
          ))}
        </div>

        {view !== 'daily' && (
          <div className="export-group">
            <button className="btn-export csv"  onClick={() => handleExport('csv')}><ArrowDownTrayIcon />CSV</button>
            <button className="btn-export xlsx" onClick={() => handleExport('xlsx')}><ArrowDownTrayIcon />XLSX</button>
          </div>
        )}
      </div>

      {/* ══════════ DAILY ══════════ */}
      {view === 'daily' && (
        <>
          <div className={`checkin-card ${cardClass}`}>
            <div>
              <div className="cc-label">Today's Status</div>
              <div className="cc-status">
                {hasOut ? 'Completed — Checked Out' : hasIn ? 'Currently In School' : 'Not Checked In Yet'}
              </div>
              {rec?.lateMinutes > 0 && <div style={{ fontSize:'.75rem', marginTop:4, opacity:.9 }}>⚠️ {rec.lateMinutes} minutes late</div>}
            </div>

            {(hasIn||hasOut) && (
              <div className="cc-times">
                <div className="cc-time"><div className="cc-tl">Check In</div><div className="cc-tv">{fmtTime(rec?.checkIn)}</div></div>
                {hasOut && <div className="cc-time"><div className="cc-tl">Check Out</div><div className="cc-tv">{fmtTime(rec?.checkOut)}</div></div>}
                {hasOut && rec?.workHours != null && <div className="cc-time"><div className="cc-tl">Hours</div><div className="cc-tv">{Number(rec.workHours||0).toFixed(2)}h</div></div>}
              </div>
            )}

            <div style={{ width:'100%', maxWidth:200 }}>
              {!hasIn         && <button className="btn-white" onClick={handleCheckIn}  disabled={actioning}><CheckCircleIcon />{actioning?'Wait…':'Check In'}</button>}
              {hasIn&&!hasOut && <button className="btn-white" onClick={handleCheckOut} disabled={actioning}><XCircleIcon />{actioning?'Wait…':'Check Out'}</button>}
              {hasOut         && <button className="btn-white" disabled>✓ Done for today</button>}
            </div>
          </div>

          {/* Today's record row in the table */}
          <div className="table-panel">
            {rec
              ? <AttTable records={[rec]} />
              : <div className="ta-empty">No record for today yet.</div>
            }
          </div>
        </>
      )}

      {/* ══════════ MONTHLY ══════════ */}
      {view === 'monthly' && (
        loading
          ? <div className="ta-loading"><div className="spinner" />Loading…</div>
          : <>
              <MonthBar label={monthLabel} summary={summary} />
              <div className="table-panel">
                {monthData?.records?.length
                  ? <AttTable records={monthData.records} />
                  : <div className="ta-empty">No records for this month.</div>
                }
              </div>
            </>
      )}

      {/* ══════════ YEARLY ══════════ */}
      {view === 'yearly' && (
        yLoading
          ? <div className="ta-loading"><div className="spinner" />Loading yearly data…</div>
          : <div className="yr-grid">
              {yearData.map(({ month: m, summary: s, records }) => {
                const present = s.present  || records.filter(r => r.status==='present').length;
                const absent  = s.absent   || records.filter(r => r.status==='absent').length;
                const onLeave = s.onLeave  || records.filter(r => r.status==='on-leave').length;
                const holiday = s.holiday  || records.filter(r => r.status==='holiday').length;
                const late    = s.totalLateMinutes ?? records.reduce((a,r) => a+(r.lateMinutes||0), 0);
                const total   = present+absent+onLeave;
                const pct     = total>0 ? Math.round((present/total)*100) : null;
                const bg  = pct===null?'#f0f2f7':pct>=90?'#ecfdf5':pct>=70?'#fffbeb':pct>0?'#fef2f2':'#f0f2f7';
                const col = pct===null?'#c0c5dc':pct>=90?'#10c98e':pct>=70?'#f59e0b':pct>0?'#ef4444':'#c0c5dc';
                const barFor = (idx) => {
                  const isThis = idx === m;
                  return <div key={idx} className="yr-m">
                    <div className="yr-m-lbl">{MONTHS[idx-1]}</div>
                    <div className="yr-m-bar" style={{ background: isThis?bg:'#f0f2f7', color: isThis?col:'#c0c5dc' }}>
                      {isThis && pct!==null ? `${pct}%` : '—'}
                    </div>
                  </div>;
                };
                return (
                  <div key={m} className="yr-card">
                    <div className="yr-card-title">{MONTHS[m-1]} {year}</div>
                    <div className="yr-months-row">{[1,2,3,4,5,6].map(barFor)}</div>
                    <div className="yr-months-row">{[7,8,9,10,11,12].map(barFor)}</div>
                    <div className="yr-totals">
                      <span className="yr-tot" style={{ color:'#10c98e' }}>✓ {present} present</span>
                      <span className="yr-tot" style={{ color:'#ef4444' }}>✗ {absent} absent</span>
                      <span className="yr-tot" style={{ color:'#f59e0b' }}>◑ {onLeave} leave</span>
                      {holiday>0 && <span className="yr-tot" style={{ color:'#8b5cf6' }}>🎉 {holiday}</span>}
                      {late>0    && <span className="yr-tot" style={{ color:'#9094a8' }}>⏱ {late} late min</span>}
                    </div>
                  </div>
                );
              })}
            </div>
      )}
    </div>
  );
};

export default TeacherAttendance;